import { TextSubHeader } from '@carboncopyco/ui';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  CustomModifierField,
  type DynamicResponseModifier,
  DynamicResponseModifiersList,
} from '../../../TransactionRuleForm';
import {
  BackIconButton,
  FormLayout,
  UpdateChangesButton,
} from '../../../TransactionRuleModal';
import { useElementSize } from '../../../hooks';
import { useEditTransactionRuleContext } from '../../EditTransactionRuleContext';
import { EditStaticResponse } from './EditStaticResponse';

export const EditTransactionModifiersStep = () => {
  const { goToGeneralSettingsStep, form } = useEditTransactionRuleContext();
  const { watch, handleSubmit, getFieldState, setValue, formState } = form;
  const [isLegalResponse, setIsLegalResponse] = useState<boolean>(true);
  const responseState = getFieldState('response');
  const dynamicResponseState = getFieldState('dynamicResponseModifiers');
  const customModifierState = getFieldState('customModifier');
  const responseType = watch('responseType');
  const dynamicResponseModifiers = watch('dynamicResponseModifiers');

  const [containerRef, { height }] = useElementSize();

  const goBack = () => {
    if (responseState.invalid || !isLegalResponse) {
      setValue('response', formState.defaultValues.response, {
        shouldDirty: true,
      });
    }

    if (customModifierState.invalid || !isLegalResponse) {
      setValue('customModifier', formState.defaultValues.customModifier, {
        shouldDirty: true,
      });
    }

    if (dynamicResponseState.invalid || !dynamicResponseModifiers.length) {
      setValue(
        'dynamicResponseModifiers',
        formState.defaultValues
          .dynamicResponseModifiers as DynamicResponseModifier[],
        {
          shouldDirty: true,
        }
      );
    }

    goToGeneralSettingsStep();
  };

  const isSubmitButtonDisabled = () => {
    if (responseType === 'dynamic') {
      return !dynamicResponseModifiers.length || !dynamicResponseState.isDirty;
    }

    if (responseType === 'custom') {
      return (
        customModifierState.invalid ||
        !customModifierState.isDirty ||
        !isLegalResponse
      );
    }

    return !responseState.isDirty || responseState.invalid || !isLegalResponse;
  };

  const fieldsToComponentMap = {
    static: () => (
      <div
        className="flex flex-1 min-h-0"
        ref={containerRef}
      >
        <EditStaticResponse
          height={height ?? 230}
          showRepresentationTypeToggle
          onError={(errors) => setIsLegalResponse(!errors.length)}
        />
      </div>
    ),
    dynamic: () => <DynamicResponseModifiersList />,
    custom: () => <CustomModifierField displayInfoMessage />,
  };

  return (
    <div>
      <div className="pt-5 px-5 flex items-center gap-2.5 text-ds-natural-700">
        <BackIconButton onClick={goBack} />
        <TextSubHeader
          color="text-ds-natural-700"
          label="Data"
        />
      </div>
      <div className="h-[656px]">
        <FormProvider {...form}>
          <FormLayout onSubmit={handleSubmit(goToGeneralSettingsStep)}>
            <FormLayout.Header />
            <div className="flex flex-col gap-1.5 flex-1 px-8 pt-7 pb-8 overflow-hidden min-h-0">
              {fieldsToComponentMap[responseType]()}
            </div>
            <FormLayout.Footer>
              <div className="flex-1" />
              <UpdateChangesButton disabled={isSubmitButtonDisabled()} />
            </FormLayout.Footer>
          </FormLayout>
        </FormProvider>
      </div>
    </div>
  );
};
